import React, { useCallback, useContext, useEffect, useState } from "react";
import { Form, Tooltip } from "antd";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { RiSearchLine, RiInformationLine } from "react-icons/ri";

import useNotification from "../../../hooks/newUXNotification";
import { useLoading } from "../../../hooks/loading";
import { DialogContext } from "../../../hooks/dialog/context";
import useProvisionTypes from "../../../hooks/useProvisionTypes";
import formatServiceTypeLabel from "../../../utils/formatServiceTypeLabel";

import {
  getUserDocument,
  createAssociateCompany,
  updateAssociateCompany,
} from "../../../redux/modules/associateCompany/actions";

import TextField from "../../../newUXComponents/TextField";
import MultiSelect from "../../../newUXComponents/MultiSelect";
import ToggleSwitch from "../../../newUXComponents/ToggleSwitch";
import Question from "../../../newUXAssets/icons/Question";
import FieldItem, {
  LabelWithTooltip,
  RequiredMark,
} from "../../../newUXComponents/FormField";

import GrantExhibitorAccessModal from "../Modal/GrantExhibitorAccessModal";

import {
  StyledDrawer,
  FooterActions,
  FooterRightActions,
  DeleteButton,
  CancelButton,
  SubmitButton,
  InlineWarning,
  InfoBox,
  InfoIcon,
  InfoTextBox,
  InfoTitle,
  InfoText,
} from "./styles";
import { INITIAL_VALUES, validation } from "./validations";

const removeNonLetterAndNumbers = (str) => str.replace(/\W/g, "");
const TOOLTIP_STYLE = { borderRadius: 6 };

const toOptionId = (value) => (value === null || value === undefined ? value : String(value));

// As opções do multi-select são os serviços que o PRÓPRIO prestador já declarou
// (data.services, vindo da busca por documento) — o VALOR enviado é sempre o id
// de Services (item.id); nunca o id do catálogo global. O RÓTULO, porém, é
// resolvido pelo catálogo (useProvisionTypes) casando por item.value — o mesmo
// padrão já usado antes desta refatoração (ex.: CreateAssociateCompany legado) —
// porque o catálogo já vem com o nome devidamente localizado. Quando não há
// correspondência, caímos para o nome cru formatado como último recurso.
const buildServiceOptions = (rawServices, catalog) => {
  const options = (rawServices || []).map((item) => {
    const catalogMatch = catalog.find((option) => option.value === item.value);
    return {
      value: toOptionId(item.id),
      label: catalogMatch?.label ?? formatServiceTypeLabel(item.name),
    };
  });

  return options.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
};

const AssociateProviderDrawer = ({ open, record, onClose, onSuccess, onDelete }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const notification = useNotification();
  const { createDialog } = useContext(DialogContext);
  const { provisionTypes: optionsProvision } = useProvisionTypes();
  const { handleLoading } = useLoading();

  const { contract, event } = useSelector((state) => state.initialSettings);

  const [loginAuthorization, setLoginAuthorization] = useState(false);
  const [serviceProviderId, setServiceProviderId] = useState();
  const [services, setServices] = useState([]);
  const [alreadyAssociated, setAlreadyAssociated] = useState(false);

  const isEditMode = Boolean(record);

  const searchParams = { event_id: event.value, opportunity_id: contract.value };

  const callbackError = useCallback(
    (message) => {
      handleLoading(false);
      notification.displayError(
        intl.formatMessage({
          id: "api.error." + message,
        }),
        "get-document-error",
      );
    },
    [notification, intl, handleLoading],
  );

  useEffect(() => {
    if (!open || !record) return;

    form.setFieldsValue({
      nameCompany: record.serviceProviderName,
      serviceTypeId: (record.serviceTypes || []).map((serviceType) =>
        toOptionId(serviceType.id),
      ),
    });
    setLoginAuthorization(!!record.allowManageAccount);

    if (record.document) {
      handleLoading(true);
      dispatch(
        getUserDocument(
          removeNonLetterAndNumbers(record.document),
          searchParams,
          (data) => {
            setServices(buildServiceOptions(data?.services, optionsProvision));
            handleLoading(false);
          },
          callbackError,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record, optionsProvision]);

  const resetState = useCallback(() => {
    form.resetFields();
    setLoginAuthorization(false);
    setServiceProviderId(undefined);
    setServices([]);
    setAlreadyAssociated(false);
  }, [form]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const callbackSuccess = useCallback(() => {
    handleLoading(false);
    notification.displaySuccess(
      intl.formatMessage({
        id: "success.create_association",
      }),
      "create_association",
    );
    resetState();
    onClose();
    onSuccess();
  }, [notification, intl, handleLoading, resetState, onClose, onSuccess]);

  const callbackEditSuccess = useCallback(() => {
    handleLoading(false);
    notification.displaySuccess(
      intl.formatMessage({
        id: "success.edit_association",
      }),
      "edit_association",
    );
    resetState();
    onClose();
    onSuccess();
  }, [notification, intl, handleLoading, resetState, onClose, onSuccess]);

  const handleModalConfirmation = (onConfirm) => {
    createDialog({
      id: "modal-confirm-create-associate",
      Component: GrantExhibitorAccessModal,
      props: {
        confirmation: onConfirm,
      },
    });
  };

  const onFinish = () => {
    const formData = form.getFieldsValue(true);

    if (isEditMode) {
      const submitEdit = () => {
        handleLoading(true);
        dispatch(
          updateAssociateCompany(
            record.serviceProviderId,
            {
              serviceTypeIds: formData.serviceTypeId,
              allowManageAccount: loginAuthorization,
            },
            { event_id: event.value, opportunity_id: contract.value },
            callbackEditSuccess,
            callbackError,
          ),
        );
      };

      if (loginAuthorization) {
        handleModalConfirmation(submitEdit);
      } else {
        submitEdit();
      }
      return;
    }

    const payload = {
      eventId: event.value,
      opportunityId: contract.value,
      serviceProviderId: serviceProviderId,
      serviceTypeIds: formData.serviceTypeId,
      allowManageAccount: loginAuthorization,
    };

    const submitCreate = () => {
      handleLoading(true);
      dispatch(createAssociateCompany(payload, callbackSuccess, callbackError));
    };

    if (loginAuthorization) {
      handleModalConfirmation(submitCreate);
    } else {
      submitCreate();
    }
  };

  const callbackGetInfoDocumentSuccess = useCallback(
    (data) => {
      if (data?.name) {
        const options = buildServiceOptions(data.services, optionsProvision);

        form.setFieldsValue({
          nameCompany: data.name,
          serviceTypeId: [],
        });

        setServiceProviderId(data.id);
        setServices(options);
        setAlreadyAssociated(!!data.alreadyAssociated);
        handleLoading(false);
      }
    },
    [form, optionsProvision, handleLoading],
  );

  const resetSearchResults = useCallback(() => {
    form.setFieldsValue({ nameCompany: undefined, serviceTypeId: [] });
    setServiceProviderId(undefined);
    setServices([]);
    setAlreadyAssociated(false);
  }, [form]);

  const handleGetUserDocument = (value) => {
    resetSearchResults();

    if (value) {
      handleLoading(true);
      dispatch(
        getUserDocument(
          removeNonLetterAndNumbers(value),
          searchParams,
          callbackGetInfoDocumentSuccess,
          callbackError,
        ),
      );
    }
  };

  return (
    <StyledDrawer
      title={intl.formatMessage({
        id: isEditMode ? "edit_provider" : "associate_provider",
      })}
      placement="right"
      width={480}
      onClose={handleClose}
      open={open}
      destroyOnClose
      footer={
        <FooterActions>
          {isEditMode ? (
            <DeleteButton type="button" onClick={() => onDelete(record)}>
              {intl.formatMessage({ id: "delete_membership" })}
            </DeleteButton>
          ) : (
            <span />
          )}
          <FooterRightActions>
            <CancelButton type="button" onClick={handleClose}>
              {intl.formatMessage({ id: "cancel" })}
            </CancelButton>
            <SubmitButton
              type="button"
              disabled={!isEditMode && alreadyAssociated}
              onClick={() => form.submit()}
            >
              {intl.formatMessage({ id: isEditMode ? "save" : "add" })}
            </SubmitButton>
          </FooterRightActions>
        </FooterActions>
      }
    >
      <Form
        layout="vertical"
        form={form}
        name="associate-provider-drawer-form"
        onFinish={onFinish}
        initialValues={INITIAL_VALUES}
      >
        {!isEditMode && (
          <TextField
            type="text"
            name="document"
            label={
              <>
                {intl.formatMessage({ id: "search_provider_by_cnpj_or_passport" })}
                <RequiredMark>*</RequiredMark>
              </>
            }
            placeholder={intl.formatMessage({ id: "search" })}
            icon={<RiSearchLine size={16} color="rgba(0, 0, 0, 0.45)" />}
            maxLength={20}
            autoComplete="off"
            onPressEnter={(e) => handleGetUserDocument(e.target.value)}
            onChange={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />
        )}

        {!isEditMode && alreadyAssociated && (
          <InlineWarning>
            {intl.formatMessage({ id: "provider_already_associated_to_contract" })}
          </InlineWarning>
        )}

        <TextField
          type="text"
          name="nameCompany"
          label={
            <>
              {intl.formatMessage({ id: "provider_name_column" })}
              <RequiredMark>*</RequiredMark>
            </>
          }
          placeholder={intl.formatMessage({ id: "provider_name_column" })}
          disabled
        />

        <FieldItem
          name="serviceTypeId"
          label={
            <LabelWithTooltip>
              {intl.formatMessage({ id: "service_types_provided" })}
              <Tooltip
                title={intl.formatMessage({ id: "tooltip.service_types_categories" })}
                overlayInnerStyle={TOOLTIP_STYLE}
              >
                <span>
                  <Question size={16} color="rgba(0, 0, 0, 0.45)" />
                </span>
              </Tooltip>
            </LabelWithTooltip>
          }
          rules={validation("serviceTypeId", intl)}
        >
          <MultiSelect
            options={services}
            placeholder={intl.formatMessage({ id: "service_types_provided" })}
          />
        </FieldItem>

        <ToggleSwitch
          checked={loginAuthorization}
          onChange={() => setLoginAuthorization(!loginAuthorization)}
          label={intl.formatMessage({ id: "cede_exhibitor_login" })}
          tooltip={intl.formatMessage({ id: "tooltip.exhibitor_login_permission" })}
        />

        {!isEditMode && (
          <InfoBox>
            <InfoIcon>
              <RiInformationLine size={18} color="#fff" />
            </InfoIcon>
            <InfoTextBox>
              <InfoTitle>
                {intl.formatMessage({ id: "how_to_hire_provider_title" })}
              </InfoTitle>
              <InfoText>
                {intl.formatMessage({ id: "how_to_hire_provider_description" })}
              </InfoText>
            </InfoTextBox>
          </InfoBox>
        )}
      </Form>
    </StyledDrawer>
  );
};

export default AssociateProviderDrawer;
